import { useEffect, useRef } from 'react';

/**
 * Temporary diagnostic: logs when components in the photo-upload path mount and
 * unmount.
 *
 * Rotating an Android phone while the file chooser is open replaces the
 * `<input>` the OS picker is holding, so the camera hands its photo back to a
 * detached node and it vanishes with no error and no request. The DOM shows the
 * subtree being rebuilt but cannot say which React component is responsible —
 * two attempts to infer that from minified class names were both wrong. This
 * says it outright.
 *
 * Remove once the culprit is fixed. It is instrumentation, not a feature.
 *
 * Off unless explicitly enabled, so it stays silent for everyone else even if
 * this reaches production:
 *
 *   localStorage.setItem('lixani:mountLog', '1')   // then reload
 */

const ENABLED_KEY = 'lixani:mountLog';

const isEnabled = () => {
  try {
    return window.localStorage.getItem(ENABLED_KEY) === '1';
  } catch (e) {
    // Storage unavailable — stay quiet.
    return false;
  }
};

/** Logs one lifecycle event. Safe to call from anywhere, including a render. */
export const logMountEvent = (event, name, detail) => {
  if (!isEnabled()) return;
  try {
    // Time first: the ordering relative to the picker opening is the whole
    // point, and console timestamps are not shown by default on Android.
    console.log(`[mountlog] ${Date.now()} ${event} ${name}`, detail ?? '');
  } catch (e) {
    // Never let a diagnostic break a render.
  }
};

/** Marks "no previous value yet", so that undefined can itself be a value. */
const NOT_SET = '__mountlog_not_set__';

/**
 * Logs mount and unmount for a function component.
 *
 * A falsy name opts out, which matters where a component has many instances
 * and only one is interesting — CrumbRoute renders around forty times, and
 * logging every one would bury the one that matters.
 */
export const useMountLog = (name) => {
  useEffect(() => {
    if (!name) return undefined;
    logMountEvent('MOUNT  ', name);
    return () => logMountEvent('UNMOUNT', name);
  }, [name]);
};

/**
 * Logs a value only when it changes. Call it during render.
 *
 * For the `key` on CrumbRoute's Route specifically: a key change is invisible
 * in a mount log, because React silently rebuilds the child while the
 * component holding the key looks entirely untouched. That is exactly the
 * shape of the bug being chased, so the key has to be watched directly.
 */
export const useChangeLog = (name, value) => {
  const previousRef = useRef(NOT_SET);

  // Same opt-out as useMountLog. The ref still has to be claimed above, because
  // hooks run unconditionally.
  if (!name) return;

  if (previousRef.current !== value) {
    const from =
      previousRef.current === NOT_SET ? '(first)' : String(previousRef.current);
    previousRef.current = value;
    logMountEvent('CHANGE ', name, `${from} -> ${String(value)}`);
  }
};

export default useMountLog;
